const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class ApiService {
  static async submitNomination(formData, files, onProgress) {
    try {
      // Step 1: Create submission without files
      if (onProgress) onProgress('Creating submission...');

      const submissionResponse = await fetch(`${API_BASE_URL}/submissions/learn-more`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!submissionResponse.ok) {
        const errorData = await submissionResponse.json();
        throw new Error(errorData.error || 'Submission failed');
      }

      const submissionResult = await submissionResponse.json();
      const submissionId = submissionResult.submission.id;

      // Step 2: Upload files if any
      if (files && Object.keys(files).length > 0) {
        if (onProgress) onProgress('Uploading documents...');

        const uploadPromises = [];

        // Map frontend file types to backend document types
        const documentTypeMapping = {
          identity: 'identityProof',
          address: 'addressProof',
          work: 'workPortfolio',
          achievements: 'certificates',
          photos: 'photos'
        };

        Object.keys(files).forEach(fileType => {
          console.log(`Processing file type: ${fileType}`, files[fileType]);
          if (files[fileType] && files[fileType].length > 0) {
            const documentType = documentTypeMapping[fileType];
            console.log(`Mapped ${fileType} to ${documentType}`);
            if (documentType) {
              console.log(`Creating upload promise for ${fileType} -> ${documentType}`);
              const uploadPromise = this.uploadFiles(submissionId, documentType, files[fileType]);
              uploadPromises.push(uploadPromise);
            } else {
              console.warn(`No mapping found for file type: ${fileType}`);
            }
          } else {
            console.log(`Skipping ${fileType} - no files or empty array`);
          }
        });

        // Wait for all uploads to complete
        await Promise.all(uploadPromises);

        if (onProgress) onProgress('Documents uploaded successfully!');
      }

      return submissionResult;
    } catch (error) {
      console.error('API submission error:', error);
      throw error;
    }
  }

  static async uploadFiles(submissionId, documentType, files) {
    try {
      console.log(`Starting upload for ${documentType}, ${files.length} files`);
      const form = new FormData();

      Array.from(files).forEach((file, index) => {
        console.log(`Adding file ${index + 1}: ${file.name} (${file.size} bytes)`);
        form.append('files', file);
      });

      const uploadUrl = `${API_BASE_URL}/uploads/${submissionId}/${documentType}`;
      console.log(`Uploading to: ${uploadUrl}`);

      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: form,
      });

      console.log(`Upload response status for ${documentType}: ${response.status}`);

      if (!response.ok) {
        const errorData = await response.json();
        console.error(`Upload failed for ${documentType}:`, errorData);
        throw new Error(errorData.error || 'File upload failed');
      }

      const result = await response.json();
      console.log(`Upload successful for ${documentType}:`, result);
      return result;
    } catch (error) {
      console.error(`API upload error for ${documentType}:`, error);
      throw error;
    }
  }

  static async getCategories(language = 'en') {
    try {
      const response = await fetch(`${API_BASE_URL}/categories-bilingual/public?lan=${language}`);

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('API categories error:', error);
      throw error;
    }
  }

  // Helper function to group awards by category
  static groupAwardsByCategory(awards) {
    const categoryMap = {
      'farmer': { title: 'Farmer Excellence', icon: 'Wheat', description: 'Recognizing outstanding farmers and agricultural practices' },
      'innovation': { title: 'Innovation & Research', icon: 'Rocket', description: 'Celebrating breakthrough innovations in agriculture' },
      'technology': { title: 'Technology & Digital', icon: 'Monitor', description: 'Honoring digital transformation in farming' },
      'sustainability': { title: 'Sustainability & Environment', icon: 'Leaf', description: 'Promoting eco-friendly agricultural practices' },
      'entrepreneurship': { title: 'Entrepreneurship', icon: 'TrendingUp', description: 'Supporting agricultural business leaders' },
      'youth': { title: 'Youth in Agriculture', icon: 'Users', description: 'Encouraging young farmers and innovators' },
      'women': { title: 'Women Empowerment', icon: 'UserCheck', description: 'Celebrating women leaders in agriculture' }
    };

    const grouped = {};

    awards.forEach(award => {
      const categoryKey = award.category;
      if (!grouped[categoryKey]) {
        const categoryInfo = categoryMap[categoryKey] || {
          title: categoryKey,
          icon: 'Trophy',
          description: 'Excellence in agriculture'
        };

        grouped[categoryKey] = {
          id: categoryKey,
          title: categoryInfo.title,
          description: categoryInfo.description,
          icon: categoryInfo.icon,
          color: this.getColorForCategory(categoryKey),
          category: categoryKey,
          awards: []
        };
      }

      grouped[categoryKey].awards.push({
        _id: award._id,
        name: award.awardName,
        isNewAward: award.isNewAward || false,
        awardType: award.awardType,
        prizeMoney: award.prizeMoney
      });
    });

    return Object.values(grouped);
  }

  // Helper function to convert backend category data to frontend format
  static convertBackendCategoryToFrontend(backendCategory) {
    // Create URL-friendly ID from award name
    const getCategoryId = (name) => {
      return name.toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .trim();
    };

    // Map backend category to frontend format
    // backendCategory is actually an award from the backend
    return {
      id: getCategoryId(backendCategory.awardName),
      objectId: backendCategory._id, // Store the actual MongoDB ObjectId
      title: backendCategory.awardName,
      description: backendCategory.description,
      icon: this.getIconForCategory(backendCategory.category),
      fee: backendCategory.prizeMoney ? `₹${backendCategory.prizeMoney.toLocaleString('en-IN')}` : '₹5,000',
      color: this.getColorForCategory(backendCategory.category),
      qualifications: backendCategory.evaluationCriteria || [],
      category: backendCategory.category,
      awardType: backendCategory.awardType,
      isNewAward: backendCategory.isNewAward || false,
      evaluationCriteria: backendCategory.evaluationCriteria || [],
      requiredDocuments: backendCategory.requiredDocuments || [],
      awards: [], // This will be empty as each item is an award itself
    };
  }

  // Helper function to get icon name for category
  static getIconForCategory(categoryType) {
    const iconMap = {
      'farmer': 'Wheat',
      'innovation': 'Rocket',
      'technology': 'Monitor',
      'sustainability': 'Leaf',
      'entrepreneurship': 'TrendingUp',
      'youth': 'Users',
      'women': 'UserCheck'
    };
    return iconMap[categoryType] || 'Trophy';
  }

  // Helper function to get color for category
  static getColorForCategory(categoryName) {
    // Alternate between the two brand colors
    const hash = categoryName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return hash % 2 === 0 ? '#3B7F3C' : '#D4A520';
  }

  // Helper function to get fee for category
  static getFeeForCategory(categoryName) {
    const feeMap = {
      'Best Crop Innovation': '₹5,000',
      'Digital Agriculture Excellence': '₹7,500',
      'Sustainable Farming Practices': '₹5,000',
      'Water Management Innovation': '₹6,000',
      'AgriTech Startup of the Year': '₹10,000',
      'Women in Agriculture Leadership': '₹3,000',
      'Young Farmer Innovator': '₹3,000',
      'Organic Farming Excellence': '₹5,000',
      'Agricultural Research & Development': '₹8,000',
      'Smart Irrigation Systems': '₹6,500',
      'Soil Health Management': '₹5,500',
      'Agricultural Mechanization': '₹7,000',
      'Pest & Disease Management': '₹5,500',
      'Agricultural Supply Chain': '₹6,000',
      'Climate-Smart Agriculture': '₹6,500',
      'Agricultural Finance Innovation': '₹7,000',
      'Livestock Management Excellence': '₹5,500',
      'Agricultural Education & Training': '₹4,500',
      'Food Safety & Quality': '₹6,000',
      'Agricultural Cooperative Success': '₹4,000'
    };
    return feeMap[categoryName] || '₹5,000';
  }

  static async getSubmissionStatus(submissionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/submissions/${submissionId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch submission status');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('API submission status error:', error);
      throw error;
    }
  }

  static async getGalleryImages(page = 1, limit = 12) {
    try {
      const response = await fetch(`${API_BASE_URL}/gallery?page=${page}&limit=${limit}`);

      if (!response.ok) {
        throw new Error('Failed to fetch gallery images');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('API gallery error:', error);
      throw error;
    }
  }

  static async getWinners() {
    try {
      const response = await fetch(`${API_BASE_URL}/winners`);

      if (!response.ok) {
        throw new Error('Failed to fetch winners');
      }

      const result = await response.json();
      // The API returns an array directly, wrap it in an object
      return { winners: result };
    } catch (error) {
      console.error('API winners error:', error);
      throw error;
    }
  }

  static async submitSponsorship(formData) {
    try {
      const response = await fetch(`${API_BASE_URL}/sponsorships`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Sponsorship submission failed');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('API sponsorship submission error:', error);
      throw error;
    }
  }

  static async checkDuplicateSubmission(phoneNumber, category) {
    try {
      const response = await fetch(`${API_BASE_URL}/submissions/check-duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber, category }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `${errorData.error}: ${errorData.message} Submission ID: ${errorData.submissionId}` 
          || 'Failed to check duplicate submission'
        );
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('API check duplicate error:', error);
      throw error;
    }
  }

  // Fetch places (district, taluk, hobli, village) from external API
  static async fetchPlaces(type, parentId) {
    try {
      const response = await fetch(`https://bhoomee.tedoratech.com/place/${type}/${parentId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch ${type}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error(`API fetch ${type} error:`, error);
      throw error;
    }
  }
}

export default ApiService;